import React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { SelectProps } from './Select.types';

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      containerClassName,
      labelClassName,
      label,
      options,
      placeholder,
      error,
      helperText,
      isRequired = false,
      isInvalid = false,
      selectSize = 'md',
      id,
      ...props
    },
    ref
  ) => {
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `${selectId}-error`;
    const helperTextId = `${selectId}-helper`;

    const showError = isInvalid || !!error;

    const sizeClasses = {
      sm: 'text-sm px-3 py-1.5 min-h-[32px]',
      md: 'text-base px-4 py-2 min-h-[40px]',
      lg: 'text-lg px-5 py-3 min-h-[48px]',
    };

    return (
      <div className={cn('w-full', containerClassName)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={selectId}
            className={cn(
              'mb-1.5 block text-sm font-medium text-gray-700 dark:text-slate-300',
              labelClassName
            )}
          >
            {label}
            {isRequired && (
              <span className="ml-1 text-red-500" aria-label="required">
                *
              </span>
            )}
          </label>
        )}

        {/* Select Container */}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            className={cn(
              'w-full appearance-none rounded-lg border bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-offset-1 dark:focus:ring-offset-slate-900',
              'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50 dark:disabled:bg-slate-900',
              'pr-10', // Space for chevron icon
              sizeClasses[selectSize],
              showError
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 dark:border-slate-600 focus:border-primary-500 focus:ring-primary-500',
              className
            )}
            aria-invalid={showError}
            aria-describedby={showError ? errorId : helperText ? helperTextId : undefined}
            aria-required={isRequired}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Chevron Icon */}
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500">
            <ChevronDown className="h-5 w-5" aria-hidden="true" />
          </div>
        </div>

        {/* Error Message */}
        {showError && error && (
          <p id={errorId} className="mt-1.5 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        {/* Helper Text */}
        {!showError && helperText && (
          <p id={helperTextId} className="mt-1.5 text-sm text-gray-500 dark:text-slate-400">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
